import { ArrowLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { z, type ZodObject, type ZodRawShape } from 'zod';
import { Logo } from '@/components/logo';

/**
 * Endereço do site da Gestora. Sem `VITE_SITE_URL`, tira o `app.` do endereço do painel
 * (app.gestorahub.com → gestorahub.com).
 */
export function siteUrl(env = import.meta.env.VITE_SITE_URL as string | undefined, host = window.location.host, protocol = window.location.protocol) {
  if (env?.trim()) return env.trim().replace(/\/$/, '');
  if (host.startsWith('app.')) return `${protocol}//${host.slice(4)}`;
  return 'https://gestorahub.com';
}

/** Acrescenta "Confirmar senha" a um schema com `password`; só confere no navegador, não vai para a API. */
export function withPasswordConfirmation<S extends ZodRawShape>(schema: ZodObject<S>) {
  const samePassword = (v: object) => {
    const { password, passwordConfirmation } = v as { password?: unknown; passwordConfirmation?: unknown };
    return password === passwordConfirmation;
  };
  return schema
    .extend({ passwordConfirmation: z.string().min(1, 'Repita a senha.') })
    .refine(samePassword, { path: ['passwordConfirmation'], message: 'As senhas não conferem.' });
}

/** Telas de entrar e criar conta: cartão de vidro claro sobre luzes suaves de marinho e roxo. */
export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
  backToSite = false,
}: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** Mostra o link "Voltar para o site" acima do logo. */
  backToSite?: boolean;
}) {
  return (
    <div className="relative flex min-h-dvh items-center justify-center overflow-hidden px-4 py-10">
      <div aria-hidden className="pointer-events-none absolute -top-40 -right-32 size-[32rem] rounded-full bg-violet/10 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -bottom-40 -left-32 size-[34rem] rounded-full bg-brand-500/15 blur-3xl" />
      {backToSite && (
        <a
          href={siteUrl()}
          className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full border border-white bg-white/70 px-3 py-1.5 text-sm font-medium text-ink/80 shadow-[0_1px_3px_rgb(30_58_95/0.08)] backdrop-blur transition hover:bg-white hover:text-ink sm:top-6 sm:left-6"
        >
          <ArrowLeft className="size-4" aria-hidden /> Voltar para o site
        </a>
      )}
      <div className="relative w-full max-w-sm">
        <div className="mb-6 flex justify-center">
          <Logo />
        </div>
        <div className="rounded-tile border border-white bg-white/80 p-7 shadow-[0_24px_60px_-32px_rgb(30_58_95/0.55)] backdrop-blur-xl backdrop-saturate-150">
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-muted">{subtitle}</p>}
          <div className="mt-6">{children}</div>
        </div>
        {footer && <div className="mt-5 text-center text-sm text-muted">{footer}</div>}
      </div>
    </div>
  );
}
